<template>
    <div
        v-if="show"
        class="modal is-active"
    >
        <base-file-delete-modal />
        <base-file-rename-modal />
        <base-file-toggle-is-public-modal/>
        <base-file-image-preview-modal
            :img-url="previewUrl"
        />

        <div class="modal-background" @click="hideModal"></div>
        <div class="modal-card">

            <header class="modal-card-head">
                <i class="modal-card-icon far fa-hdd"></i>
                <p class="modal-card-title">
                    {{  title }}
                </p>
                <button class="delete" aria-label="close" @click="hideModal"></button>
            </header>

            <section
                class="modal-card-body p-0"
            >
                <base-file-list/>
            </section>

            <footer class="modal-card-foot">
                <div class="is-flex file-management-top-panel has-background-white-ter" v-if="isInsertMode">
                    <div class="column">
                        <p>{{ trans.add_file_public_help }}</p>
                    </div>
                </div>

                <div class="is-flex file-management-top-panel has-background-white-ter">
                    <disk-space-indicator
                        :measures="trans.size_measures"
                        :msg="trans.used_disk_space"
                        :total-space="totalBaseDiskSpace"
                        :used-space="usedBaseDiskSpace"
                        class="column is-one-third-desktop is-half-tablet"
                    />
                    <div class="is-flex column is-two-thirds-desktop">
                        <div class="is-flex ml-auto is-fullheight file-management-top-panel-buttons">
                            <transition name="fade">
                                <div
                                    v-if="isSelectedFile"
                                >
                                    <span
                                        v-show="isImageSelected"
                                        :title="trans.preview_button"
                                        @click="showFilePreviewModal"
                                    >
                                        <i class="fas fa-lg fa-eye"></i>
                                    </span>
                                    <a
                                        :title="trans.download_button"
                                        :href="downloadUrl"
                                        download
                                    >
                                        <i
                                            class="fas fa-lg fa-file-download"
                                        ></i>
                                    </a>
                                    <span
                                        :title="trans.rename_button"
                                        @click="showFileRenameModal"
                                    >
                                        <i
                                            class="fas fa-lg"
                                            :class="isRenamingFile ? 'fa-spinner fa-spin' : 'fa-pen'"
                                        ></i>
                                    </span>
                                    <span
                                        :title="trans.delete_button"
                                        @click="showFileDeleteModal"
                                    >
                                        <i
                                            class="fas fa-lg"
                                            :class="isDeletingFile ? 'fa-spinner fa-spin' : 'fa-trash-alt'"
                                        ></i>
                                     </span>
                                </div>
                            </transition>
                        </div>

                    </div>
                </div>
                <div class="additional-control-panel p-1">
                    <base-file-upload-button/>
                    <span class="has-text-grey-light is-size-7">
                        ({{ mimesList }})
                    </span>
                </div>
                <div class="field has-addons">
                    <p class="control" v-if="isInsertMode">
                        <button
                            class="button is-info"
                            @click="insertFile"
                            :disabled="!isSelectedFile"
                        >
                            {{  trans.insert_button }}
                        </button>
                    </p>
                    <p class="control">
                        <button
                            class="button ml-auto"
                            @click="hideModal"
                        >
                            {{  trans.close }}
                        </button>
                    </p>
                </div>
            </footer>
        </div>
    </div>
</template>

<script>
import {mapActions, mapGetters, mapState} from "vuex";
import BaseFileUploadButton from "./BaseFileUploadButton";
import BaseFileList from "./BaseFileList";
import DiskSpaceIndicator from "./DiskSpaceIndicator";
import BaseInviteAcceptModal from "../BaseInviteAcceptModal";
import BaseFileDeleteModal from "./BaseFileDeleteModal";
import BaseFileRenameModal from "./BaseFileRenameModal";
import BaseFileImagePreviewModal from "./BaseFileImagePreviewModal";
import BaseFileToggleIsPublicModal from "./BaseFileToggleIsPublicModal";

export default {
    name: "BaseFileManagementModal",
    props: {
        initTrans: Object,
        initActions: Object,
        mimes: String,
    },
    components: {
        BaseFileToggleIsPublicModal,
        BaseFileImagePreviewModal,
        BaseFileRenameModal,
        BaseFileDeleteModal,
        BaseInviteAcceptModal,
        DiskSpaceIndicator,
        BaseFileList,
        BaseFileUploadButton
    },
    mounted() {
        this.setActions(this.initActions);
        this.setTrans(this.initTrans);
    },
    computed: {
        ...mapState('fileManagement', {
            show: 'fileManagementModalIsShown'
        }),
        ...mapState('fileManagement', [
            'trans',
            'actions',
            'totalBaseDiskSpace',
            'usedBaseDiskSpace',
            'isUploadingFile',
            'selectedFile',
            'isInsertMode',
        ]),
        ...mapState('editor', ['editor']),
        ...mapGetters('fileManagement', [
            'isSelectedFile',
            'isRenamingFile',
            'isDeletingFile',
        ]),
        downloadUrl() {
            if (!this.isSelectedFile) {
                return;
            }

            return this.getFileDownloadUrl(this.selectedFile);
        },
        previewUrl() {
            if (!this.isSelectedFile) {
                return;
            }

            return this.getFileUrl(this.selectedFile);
        },
        isImageSelected() {
            return this.isImageFile(this.selectedFile);
        },
        title() {
            return this.isInsertMode
                ? this.trans.insert_mode_title
                : this.trans.title;
        },
        mimesList() {
            return this.mimes.replaceAll(',', ', ');
        }
    },
    methods: {
        ...mapActions('fileManagement', {
            hideModal: 'hideFileManagementModal'
        }),
        ...mapActions('fileManagement', [
            'showFileDeleteModal',
            'showFileRenameModal',
            'showFilePreviewModal',
            'setActions',
            'setTrans',
        ]),

        /**
         * Обработчик нажатия кнопки вставить файл
         */
        insertFile() {
            let {content, selector} = this.getFileHtml(this.selectedFile);

            this.editor.insertContent(content);
            this.hideModal();

            this.editor.selection.select(this.editor.dom.select(selector).pop());
            sendGAEvent('Файл вставлен в текст документа');
        },
    },
};
</script>

<style lang="scss" scoped>
@import 'resources/sass/variables';

.modal {
    z-index: 1400;
}
section {
    scroll-behavior: smooth;
}

.modal-card-foot {
    flex-direction: column;
    padding: 0 0 20px 0;
    border-top: 2px solid #dbdbdb;
}

.file-management-top-panel {
    border-bottom: 1px solid #dbdbdb;
    width: 100%;
}

.additional-control-panel {
    width: 100%;
}

.file-management-top-panel-buttons {
    align-items: center;

    span,
    a {
        padding: 0.7rem 1rem;

        i {
            color: $bright-grey;
        }

        &:hover {
            i {
                color: $deep-sky-blue;
            }

            cursor: pointer;
        }
    }
}
</style>
